const React = require('react');
const T = require('prop-types');
const { default: styled } = require('styled-components');
const Moment = require('moment/moment');

const { default: Classes } = require('./styles.scss');
const Avatar = require('components/ProfileAvatar');
const { default: ListItem } = require('@mui/material/ListItem');
const { default: ListItemAvatar } = require('@mui/material/ListItemAvatar');
const { default: ListItemText } = require('@mui/material/ListItemText');
const { default: ListItemSecondaryAction } = require('@mui/material/ListItemSecondaryAction');
const { default: RightArrowIcon } = require('@mui/icons-material/ChevronRight');

const UserBadge = require('containers/UserBadge');
const { getSizedImageUrl } = require('utils/image');
const SUR = require('utils/should-update-record');

const internals = {
    propTypes: {
        user: T.object.isRequired,
        secondaryText: T.element,
        dm: T.object,
        lastMessage: T.object,
        previewSignature: T.string,
        leftAvatar: T.element,
        showUserActions: T.bool,
        canViewBadge: T.bool,
        link: T.string,
        onClick: T.func,
        hasContainerElement: T.bool
    }
};

module.exports = class UserListItem extends React.Component {

    // NOTE this component implements shouldComponentUpdate()

    // NOTE propTypes defined above in order to work around
    // babel/webpack limitations, referencing static props within a class.
    static propTypes = internals.propTypes

    static defaultProps = {
        showUserActions: false
    };

    constructor() {

        super();

        this.propNames = Object.keys(internals.propTypes);
    }

    // Essentially identical to PureComponent,
    // but for the user prop only pays attention to id.
    shouldComponentUpdate(nextProps) {

        for (let i = 0; i < this.propNames.length; ++i) {
            const name = this.propNames[i];
            if (name === 'user') {
                if (SUR.shouldUpdate(this.props[name], nextProps[name])) {
                    return true;
                }
            }
            else if (this.props[name] !== nextProps[name]) {
                return true;
            }
        }

        return false;
    }

    render() {

        const {
            user,
            secondaryText,
            leftAvatar,
            canViewBadge,
            dm,
            lastMessage,
            previewSignature,
            rightArrowAriaLabel,
            link,
            onClick,
            push,
            ...rest
        } = this.props;

        const {
            StyledListItem,
            StyledListItemAvatar,
            PrimaryTextRow,
            PrimaryTextTimestamp,
            NameAndBadgeContainer,
            StyledListItemText
        } = internals;

        const resolvedLastMessage = lastMessage || dm?.lastMessage;

        const _onClick = (evt) => {

            const targetTagName = evt?.target?.tagName?.toLowerCase();

            if (targetTagName === 'img' || targetTagName === 'button' || targetTagName === 'a') {
                evt.preventDefault();
                return;
            }

            // Run onClick if exists
            onClick?.();

            if (link) {
                push(link);
            }
        };

        return (
            <StyledListItem
                onClick={_onClick}
                {...rest}
            >
                <StyledListItemAvatar>
                    {leftAvatar || (
                        <Avatar src={getSizedImageUrl(user.croppedPicture, 100)} alt='User profile picture' />
                    )}
                </StyledListItemAvatar>
                <StyledListItemText
                    primary={<PrimaryTextRow>
                        <NameAndBadgeContainer>
                            {`${user.firstName} ${user.lastName}`}<span className={`${user.enabled ? '' : Classes.userDisabled}`} />
                            {canViewBadge && <UserBadge mini className={Classes.userBadge} userType={user.type} />}
                        </NameAndBadgeContainer>
                        {resolvedLastMessage && <PrimaryTextTimestamp>
                            {Moment(resolvedLastMessage.timestamp)
                                .calendar(null, {
                                    lastDay : '[Yesterday] [at] LT',
                                    sameDay : '[Today] [at] LT',
                                    nextDay : '[Tomorrow]',
                                    lastWeek : 'dddd [at] LT',
                                    nextWeek : 'dddd',
                                    sameElse : 'MM/DD/YYYY [at] LT'
                                })
                            }
                        </PrimaryTextTimestamp>}
                    </PrimaryTextRow>}
                    secondary={secondaryText}
                />
                <ListItemSecondaryAction component='div' style={{ pointerEvents: 'none', right: 5 }}>
                    <RightArrowIcon />
                </ListItemSecondaryAction>
            </StyledListItem>
        );
    }
};

internals.StyledListItem = styled(ListItem)`
    min-height: 66px;
    padding-left: 10px;
    padding-right: 0;
    padding-bottom: 6px;

    && {
        min-height: 66px;
    }

    display: flex;
    flex-flow: row nowrap;

    position: relative;

    // border-bottom
    &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 72px; // Adjust inset from the left
        width: calc(100% - 72px); // Adjust width to span only part of the element
        height: 1px;
        background-color: ${({ theme }) => theme.palette.list.divider};
    }

    .MuiListItem-container {
        flex: 1;
    }

    .MuiAvatar-root {
        width: 46px;
        height: 46px;
        background: #fff;
        border: 1px solid rgba(18, 39, 79, 0.12);
        box-sizing: border-box;
        transition: background 120ms ease;
    }

    &:hover .MuiAvatar-root {
        background: #fff !important;
    }

    .MuiListItemSecondaryAction-root {
        right: 5px !important;
    }

    .MuiListItemSecondaryAction-root .MuiSvgIcon-root {
        color: #000 !important;
        fill: #000 !important;
        opacity: 1;
    }

    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.palette.menu.hover};
    }
`;

internals.StyledListItemAvatar = styled(ListItemAvatar)`
    && {
        position: relative;
        align-self: center;

        .MuiAvatar-circular,
        .MuiAvatar-root {
            background: white;
            box-shadow: none;
        }
    }
`;

internals.PrimaryTextRow = styled.span`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    box-sizing: border-box;
    padding-right: 108px;
`;

internals.PrimaryTextTimestamp = styled.span`
    position: absolute;
    top: -2px;
    right: 0px;
    color: #5f6368;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.5;
    white-space: nowrap;
`;

internals.NameAndBadgeContainer = styled.span`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    color: inherit;
    font-size: 17px;
    font-weight: 500;
    line-height: 22px;
`;

internals.StyledListItemText = styled(ListItemText)`
    align-self: center;
    min-width: 0;
    margin: 4px 0;
    padding-right: 36px;

    .MuiListItemText-primary {
        line-height: 22px;
    }

    .MuiListItemText-secondary {
        margin-top: 6px;
    }
`;
